import { useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { useSchemaToGrid } from '../../../../util/schema-utils';
import { ICaptableInvestmentDataModel } from '../../../../data-models/captable2.data-model';
import { ShareClass } from '../../../../view-models/captable.view-model';
import {
  CapTableInvestmentType,
  captableInvestmentGridSchema,
} from '../../../../schemas/CaptableInvestment.schema';
import { withOverrides } from '../../../../util/ag-grid-utils';
import { ColDefOverrides } from './ColDefOverrides';

const noteBlockColumns = [
  'name',
  'cashRaised',
  'principal',
  'interest',
  'discountRate',
  'interestRate',
  'valuationCap',
  'interestType',
  'compoundingPeriod',
  'maturityDate',
  'investmentDate',
] as const;

const shareClassColumns = [
  'name',
  'cashRaised',
  'fullyDilutedShares',
  'pricePerShare',
  'seniority',
  'pariPassu',
  'participationCap',
  'liquidationMultiplier',
  'liquidationPreference',
  'conversionRatio',
  'conversionPrice',
  'investmentDate',
] as const;

const warrantColumns = ['name', 'fullyDilutedShares', 'strikePrice', 'investmentDate'] as const;

const cashColumns = ['name', 'cashRaised'] as const;

// Debt covers both plain cash debt and the two loan subtypes (Term Loan,
// Employee Option Loan), discriminated by securityType. strikePrice and
// borrowerName populate only for Employee Option Loans; principal / maturity
// / interestRate cover both loan types. Plain debt rows just show cashRaised.
const debtColumns = [
  'name',
  'securityType',
  'principal',
  'cashRaised',
  'interestRate',
  'maturityDate',
  'strikePrice',
  'borrowerName',
  'investmentDate',
] as const;

const optionsColumns = [
  'name',
  'authorizedShares',
  'fullyDilutedShares',
  'outstandingEquityAwardDerivatives',
  'totalOptionPool',
  'outstandingCommittedRestrictedStockAwards',
  'strikePrice',
  'investmentDate',
] as const;

const TypeToColumns: Record<CapTableInvestmentType, readonly string[]> = {
  'share-class': shareClassColumns,
  'warrant-block': warrantColumns,
  'note-block': noteBlockColumns,
  'option-pool': optionsColumns,
  cash: cashColumns,
  debt: debtColumns,
};

export function useInvestmentColDefs(type: CapTableInvestmentType): ColDef<ICaptableInvestmentDataModel>[] {
  const schemaToColDef = useSchemaToGrid();

  return useMemo(() => {
    let defs;
    if (type === 'share-class') {
      const participationTypeField: ColDef<ICaptableInvestmentDataModel> = {
        colId: 'participationType',
        field: 'participationType',
        headerName: 'Participation Type',
      };
      const columns = TypeToColumns[type] as (keyof ICaptableInvestmentDataModel)[];
      const allFields = schemaToColDef(captableInvestmentGridSchema(), columns);

      const insertIndex = allFields.findIndex((cd) => cd.field === 'pariPassu');
      defs = [
        ...allFields.slice(0, insertIndex),
        participationTypeField,
        ...allFields.slice(insertIndex),
      ] as ColDef<ShareClass | ICaptableInvestmentDataModel>[];
    } else {
      const columns = TypeToColumns[type] as (keyof ICaptableInvestmentDataModel)[];
      defs = schemaToColDef(captableInvestmentGridSchema(), columns);
    }

    return withOverrides(
      defs as ColDef<ICaptableInvestmentDataModel>[],
      ColDefOverrides
    ) as ColDef<ICaptableInvestmentDataModel>[];
  }, [schemaToColDef, type]);
}

export const DefaultColDefs = {
  flex: 1,
  minWidth: 150,
};

/**
 * Computes liquidation preference for a captable investment.
 * Only applies to preferred share classes; returns null for all other types.
 * Returns null when cashRaised is null/undefined (unknown) to keep the cell blank.
 * If liquidationMultiplier is null/missing, defaults to 1 (result = cashRaised).
 */
export function getLiquidationPreference(
  data:
    | Pick<ICaptableInvestmentDataModel, 'shareClassType' | 'cashRaised' | 'liquidationMultiplier'>
    | undefined
): number | null {
  if (!data || data.shareClassType !== 'preferred') {
    return null;
  }
  if (data.cashRaised == null) {
    return null;
  }
  const multiplier = data.liquidationMultiplier ?? 1;
  return data.cashRaised * multiplier;
}
